import { keepPreviousData } from '@tanstack/query-core'
import { InfiniteData, useQueryClient } from '@tanstack/react-query'
import moment from 'moment-timezone'
import queryString from 'query-string'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import {
  CreateCreditCardVerificationSuccessMessage,
  NewVerificationCodeSentToastMessage,
} from 'src/app/Toast/Toast'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import {
  CreditCardListModel,
  CreditCardListResponse,
  CreditCardStatus,
} from 'src/models/creditCard.models'
import {
  CreditCardPermissionsModel,
  CreditCardPermissionsResponse,
} from 'src/models/creditCardPermissions.models'
import {
  IssuerIntegratorListModel,
  IssuerIntegratorListResponse,
} from 'src/models/issuerIntegrator.models'
import { PaginationResponse } from 'src/models/pagination.models'
import {
  creditCardsQueryKeys,
  issuerQueryKeys,
} from 'src/queries/queries.constants'
import {
  ExtendBaseInfiniteQueryOptions,
  ExtendBaseQueryOptions,
  getNextExtendPageParam,
  MutationMethod,
  useExtendInfiniteQuery,
  useExtendMutation,
  useExtendQuery,
} from 'src/queries/queries.utils'
import {
  GET_CREDIT_CARD_INIT,
  GET_CREDIT_CARD_PERMISSIONS_FAILURE,
  GET_CREDIT_CARD_PERMISSIONS_INIT,
  GET_CREDIT_CARD_PERMISSIONS_SUCCESS,
  GET_CREDIT_CARD_SUCCESS,
  GET_CREDIT_CARDS_FAILURE,
  GET_CREDIT_CARDS_INIT,
  GET_CREDIT_CARDS_SUCCESS,
  getCreditCardsStatisticsActiveBudgetCount,
  getCreditCardsStatisticsActiveVcnCount,
  getCreditCardsStatisticsBudgetCount,
  getCreditCardsStatisticsVcnCount,
  POST_CREDIT_CARD_START_VERIFICATION_SUCCESS,
  POST_CREDIT_CARD_VERIFY_SUCCESS,
} from 'src/redux/actions/data/creditCards.actions'
import { GET_VIRTUAL_CARD_FAILURE } from 'src/redux/actions/data/virtualCards.actions'
import { selectStatisticsDispatcherFromState } from 'src/redux/selectors/app.selectors'
import { GetCreditCardsRequest } from 'src/requests/getCreditCards.request'

const cacheTimestamp = () => moment().format('dddd, Do MMM YYYY, h:mm:ss A')

interface QueryIssuerOptions {
  issuerId: string | undefined
  enabled?: boolean
}
export const useQueryIssuer = (options: QueryIssuerOptions) => {
  const { issuerId } = options
  const enabled = options?.enabled ?? true

  return useExtendQuery<
    IssuerIntegratorListResponse,
    IssuerIntegratorListModel
  >({
    queryKey: issuerQueryKeys.detail(issuerId),
    queryUrl: () => `/issuers/${issuerId}`,
    enabled: enabled && issuerId !== undefined,
    select: (data) => new IssuerIntegratorListModel(data),
  })
}

interface QueryCreditCardOptions
  extends ExtendBaseQueryOptions<
    { creditCard: CreditCardListResponse },
    CreditCardListModel
  > {
  creditCardId: string | undefined
}

export const useQueryCreditCard = ({
  creditCardId,
  enabled = true,
  retry,
  ...rest
}: QueryCreditCardOptions) => {
  const dispatch = useDispatch()
  return useExtendQuery({
    queryKey: creditCardsQueryKeys.detail(creditCardId),
    queryUrl: () => `/creditcards/${creditCardId}`,
    onSuccess: (data) => {
      // TODO: remove when everything is moved to react query
      dispatch({ type: GET_CREDIT_CARD_SUCCESS, payload: data })
      dispatch(
        getCreditCardsStatisticsActiveBudgetCount(
          [data.creditCard.id],
          cacheTimestamp(),
        ),
      )
      dispatch(
        getCreditCardsStatisticsBudgetCount(
          [data.creditCard.id],
          cacheTimestamp(),
        ),
      )
      dispatch(getCreditCardsStatisticsActiveVcnCount([data.creditCard.id]))
      dispatch(getCreditCardsStatisticsVcnCount([data.creditCard.id]))
    },
    enabled: enabled && creditCardId !== undefined,
    // TODO: we currently rely on the 404 error for the use-case of a user not having permission
    // and this is an anti-pattern against how react query usually handles error states
    // but this imitates behaviour today of making a single fetch when the user opens the flyout
    retry,
    select: (data) => new CreditCardListModel(data.creditCard),
    ...rest,
    // redux <-> react query interop
    reduxInitAction: { type: GET_CREDIT_CARD_INIT },
    onError: (error) => {
      dispatch({ type: GET_VIRTUAL_CARD_FAILURE, payload: error })
    },
  })
}

interface QueryCreditCardPermissionsOptions
  extends ExtendBaseQueryOptions<
    CreditCardPermissionsResponse,
    CreditCardPermissionsModel
  > {
  creditCardId: string | undefined
}

export const useQueryCreditCardPermissions = ({
  creditCardId,
  enabled = true,
  retry,
  ...rest
}: QueryCreditCardPermissionsOptions) => {
  const dispatch = useDispatch()
  return useExtendQuery({
    queryKey: creditCardsQueryKeys.permissions(creditCardId),
    queryUrl: () => `/creditcards/${creditCardId}/permissions`,
    enabled: enabled && creditCardId !== undefined,
    // TODO: we currently rely on the 404 error for the use-case of a user not having permission
    // and this is an anti-pattern against how react query usually handles error states
    // but this imitates behaviour today of making a single fetch when the user opens the flyout
    retry,
    select: (data) => new CreditCardPermissionsModel(data),
    ...rest,
    // redux <-> react query interop
    reduxInitAction: { type: GET_CREDIT_CARD_PERMISSIONS_INIT },
    onSuccess: (data) => {
      dispatch({ type: GET_CREDIT_CARD_PERMISSIONS_SUCCESS, payload: data })
    },
    onError: (error) => {
      dispatch({ type: GET_CREDIT_CARD_PERMISSIONS_FAILURE, payload: error })
    },
  })
}

export interface CreditCardsResponse {
  pagination: PaginationResponse
  creditCards: CreditCardListResponse[]
}

export interface CreditCardsDataModel {
  creditCards: CreditCardListModel[]
  page: number
  totalItems: number
}

const selectCreditCardListModel = (
  response: InfiniteData<CreditCardsResponse, number>,
): CreditCardsDataModel => ({
  creditCards: response?.pages.flatMap((page) =>
    page.creditCards.map((creditCard) => new CreditCardListModel(creditCard)),
  ),
  page: response.pageParams.length - 1,
  totalItems: response?.pages[0]?.pagination.totalItems,
})

interface QueryCreditCardsOptions
  extends ExtendBaseInfiniteQueryOptions<
    CreditCardsResponse,
    number,
    CreditCardsDataModel
  > {
  filters: GetCreditCardsRequest | undefined
  additionalKey?: string
}

export const useQueryCreditCards = ({
  filters,
  enabled = true,
  queryCacheOptions,
  ...rest
}: QueryCreditCardsOptions) => {
  const dispatch = useDispatch()
  return useExtendInfiniteQuery({
    queryKey: creditCardsQueryKeys.list(filters, rest.additionalKey),
    queryUrl: ({ pageParam }) => {
      return `creditcards?${queryString.stringify({ ...filters, page: pageParam })}`
    },
    enabled: enabled && filters !== undefined,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => getNextExtendPageParam(lastPage.pagination),
    select: selectCreditCardListModel,
    // re-fetches happen in place without resetting back to an empty state, less flashing of content
    placeholderData: keepPreviousData,
    ...queryCacheOptions,
    ...rest,
    // react query <-> redux interop
    reduxInitAction: { type: GET_CREDIT_CARDS_INIT },
    onSuccess: (data) => {
      // TODO: lots of side-effects in creditCards.actions.tsx that need to be implemented for a full refactor
      dispatch({
        type: GET_CREDIT_CARDS_SUCCESS,
        payload: { ...data, queryParameters: filters },
      })
    },
    onError: (error) => {
      dispatch({
        type: GET_CREDIT_CARDS_FAILURE,
        payload: { ...error, queryParameters: filters },
      })
    },
  })
}

interface QueryCreditCardsPaginatedOptions
  extends ExtendBaseQueryOptions<CreditCardsResponse, CreditCardsDataModel> {
  filters: GetCreditCardsRequest | undefined
}

export const useQueryCreditCardsPaginated = ({
  filters,
  enabled = true,
  queryCacheOptions,
  ...rest
}: QueryCreditCardsPaginatedOptions) => {
  const dispatch = useDispatch()
  return useExtendQuery({
    queryKey: creditCardsQueryKeys.list(filters),
    queryUrl: () => `creditcards?${queryString.stringify({ ...filters })}`,
    enabled: enabled && filters !== undefined,
    select: (data) => ({
      creditCards: data.creditCards.map(
        (creditCard) => new CreditCardListModel(creditCard),
      ),
      page: data.pagination.page,
      totalItems: data.pagination.totalItems,
    }),
    ...queryCacheOptions,
    ...rest,
    // react query <-> redux interop
    reduxInitAction: { type: GET_CREDIT_CARDS_INIT },
    onSuccess: (data) => {
      // TODO: lots of side-effects in creditCards.actions.tsx that need to be implemented for a full refactor
      dispatch({
        type: GET_CREDIT_CARDS_SUCCESS,
        payload: { ...data, queryParameters: filters },
      })
    },
    onError: (error) => {
      dispatch({
        type: GET_CREDIT_CARDS_FAILURE,
        payload: { ...error, queryParameters: filters },
      })
    },
  })
}

export const useVerifyCreditCard = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  return useExtendMutation<
    { creditCard: CreditCardListResponse },
    { creditCardId: string; body: { code: string } }
  >(MutationMethod.POST, {
    mutationUrl: ({ creditCardId }) => `/creditcards/${creditCardId}/verify`,
    onSuccess: (data) => {
      if (data.creditCard.status !== CreditCardStatus.Blocked) {
        dispatchStatistics(
          [
            appStatisticsKeys.CreditCardsCanIssueVirtualCardsCountStatistics,
            appStatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCountStatistics,
          ],
          cacheTimestamp(),
        )
        queryClient.invalidateQueries({
          queryKey: creditCardsQueryKeys.detail(data.creditCard.id),
        })
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: <CreateCreditCardVerificationSuccessMessage />,
          }),
        )
      }
      dispatch({ type: POST_CREDIT_CARD_VERIFY_SUCCESS, payload: data })
    },
  })
}

export const useStartCreditCardVerification = () => {
  const dispatch = useDispatch()

  return useExtendMutation<
    { creditCard: CreditCardListResponse },
    { creditCardId: string; body: Record<string, never> }
  >(MutationMethod.POST, {
    mutationUrl: ({ creditCardId }) =>
      `/creditcards/${creditCardId}/startverification`,
    onSuccess: (data) => {
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: <NewVerificationCodeSentToastMessage />,
        }),
      )
      dispatch({
        type: POST_CREDIT_CARD_START_VERIFICATION_SUCCESS,
        payload: data,
      })
    },
  })
}
